/** `/login/totp`: second factor after the magic link / dev code (MFA for staff, §17.1). */
import { Button, Field, Input } from '@windrop/ui';
import { useState, type FormEvent } from 'react';
import { Link } from 'react-router';
import { identityApi } from '../../lib/api/admin';
import { isApiError } from '../../lib/api/client';
import { AuthLayout } from './AuthLayout';
import { useContinueLogin, useLoginFlow } from './Verify';

export function TotpPage() {
  const flow = useLoginFlow();
  const continueLogin = useContinueLogin();
  const [totp, setTotp] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (!flow || !flow.code) {
    return (
      <AuthLayout title="Sign-in step missing">
        <p>Your sign-in link must be verified first.</p>
        <Button href="/login">Back to sign in</Button>
      </AuthLayout>
    );
  }

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!/^\d{6}$/.test(totp)) {
      setError('Enter the 6-digit code from your authenticator app.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      continueLogin(
        await identityApi.verify({ challengeId: flow.challengeId, code: flow.code ?? '', totp }),
        flow,
        flow.code ?? '',
      );
    } catch (err) {
      setError(isApiError(err) ? err.message : 'That code was not accepted. Codes rotate every 30 seconds.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthLayout title="Authenticator code">
      <form onSubmit={submit} className="wd-stack" noValidate>
        <Field label="6-digit code" required hint="From your authenticator app.">
          <Input
            preset="otp"
            value={totp}
            onChange={(e) => setTotp(e.target.value.replace(/\D/g, '').slice(0, 6))}
            maxLength={6}
            autoFocus
          />
        </Field>
        {error ? (
          <p role="alert" className="text-error">
            {error}
          </p>
        ) : null}
        <Button type="submit" loading={busy} loadingLabel="Verifying" block>
          Finish sign-in
        </Button>
        <p className="wd-muted">
          Lost your device? <Link to="/login">Ask your organizer admin to reset MFA</Link>
        </p>
      </form>
    </AuthLayout>
  );
}
